import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  PersonAddAlt1 as AssignIcon,
} from '@mui/icons-material';

interface ActionMenuProps {
  ticket: any;
  authUser: any;
  onView: (ticket: any) => void;
  onEditTicketOpen: (ticket: any) => void;
  onOpenMessages: (ticket: any) => void;
  onEdit: (ticket: any) => void;
  onDelete: (ticketId: number, ticketSubject?: string) => void;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  ticket,
  authUser,
  onView,
  onEditTicketOpen,
  onOpenMessages,
  onEdit,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleClose();
  };

  const showSnackbar = (message: string, type: 'success' | 'error' | 'warning' = 'warning') => {
    console.log(`${type}: ${message}`);
  };

  const menuItemStyles = {
    fontSize: '0.75rem',
    justifyContent: 'flex-start',
    py: 0.1,
    px: 1,
    minHeight: '28px',
    borderRadius: '4px',
    margin: '0 2px',
    width: 'calc(100% - 8px)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiListItemIcon-root': {
      minWidth: '20px',
      marginRight: '4px',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '14px',
    },
    '& .MuiTouchRipple-root': {
      width: '100%',
    }
  };

  const listItemTextStyles = {
    '& .MuiTypography-root': {
      fontSize: '0.75rem',
      fontWeight: 400,
      textAlign: 'left',
      lineHeight: 1.2,
    },
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        sx={{ 
          color: 'primary.main',
          padding: '5px',
          '& .MuiSvgIcon-root': {
            fontSize: '20px'
          }
        }}
        aria-label="işlemler"
      >
        <MoreVertIcon />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            minWidth: 125,
            borderRadius: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            py: 0.5,
            overflow: 'visible',
            '& .MuiList-root': {
              padding: '4px 0',
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Detayları Gör */}
        <MenuItem 
          onClick={() => handleMenuAction(() => onView(ticket))}
          sx={menuItemStyles}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" sx={{ color: '#2e7d32' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Detayları Gör" 
            sx={listItemTextStyles}
          />
        </MenuItem>

        <Divider sx={{ my: 0.2, opacity: 0.4 }} />

        {/* Talep Düzenle */}
        {(authUser?.role_id === 1 || authUser?.role_id === 2 || authUser?.role_id === 3) && (
          <MenuItem 
            onClick={() => {
              if (authUser?.role_id === 3 && ticket.status_id !== 1) {
                showSnackbar('Talep incelenmeye başlandı, düzenleyemezsiniz. Yeni talep oluşturabilirsiniz.', 'warning');
                handleClose();
                return;
              }
              handleMenuAction(() => onEditTicketOpen(ticket));
            }}
            disabled={authUser?.role_id === 3 && ticket.status_id !== 1}
            sx={menuItemStyles}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" sx={{ 
                color: authUser?.role_id === 3 && ticket.status_id !== 1 ? '#ccc' : '#1976d2',
              }} />
            </ListItemIcon>
            <ListItemText 
              primary="Talep Düzenle" 
              sx={listItemTextStyles}
            />
          </MenuItem>
        )}

        <Divider sx={{ my: 0.2, opacity: 0.4 }} />

        {/* Notlar ve Mesajlar */}
        <MenuItem 
          onClick={() => handleMenuAction(() => onOpenMessages(ticket))}
          sx={menuItemStyles}
        >
          <ListItemIcon>
            <ChatIcon fontSize="small" sx={{ color: '#9c27b0' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Notlar ve Mesajlar" 
            sx={listItemTextStyles}
          />
        </MenuItem>

        <Divider sx={{ my: 0.2, opacity: 0.4 }} />

        {/* Talebi Yönet / Talep Kapat */}
        {(authUser?.role_id === 1 || authUser?.role_id === 2 || authUser?.role_id === 3) && (
          <MenuItem 
            onClick={() => handleMenuAction(() => onEdit(ticket))}
            disabled={authUser?.role_id === 3 && ticket.status_id === 8}
            sx={menuItemStyles}
          >
            <ListItemIcon>
              <AssignIcon fontSize="small" sx={{ 
                color: authUser?.role_id === 3 && ticket.status_id === 8 ? '#ccc' : '#6d4c41',
              }} />
            </ListItemIcon>
            <ListItemText 
              primary={authUser?.role_id === 3 ? 'Talebi Kapat' : 'Talebi Yönet'} 
              sx={listItemTextStyles}
            />
          </MenuItem>
        )}

        {/* Talebi Sil - SADECE ADMIN */}
        {authUser?.role_id === 1 && (
          <>
            <Divider sx={{ my: 0.2, opacity: 0.4 }} />
            <MenuItem 
              onClick={() => handleMenuAction(() => onDelete(ticket.id, ticket.subject))}
              sx={{
                ...menuItemStyles,
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Talebi Sil" 
                sx={{
                  ...listItemTextStyles,
                  '& .MuiTypography-root': {
                    ...listItemTextStyles['& .MuiTypography-root'],
                    color: 'error.main',
                  }
                }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};